// ======================================================
// ✅ APLICACIÓN PRINCIPAL – Evalúa Salud IPS
// Autor: Breyner Javier Montalvo Peñarredonda
// ------------------------------------------------------
// Rutas protegidas, lazy loading, persistencia de última
// ruta y redirección por defecto al módulo de Activos.
// Integración completa del módulo de Usuarios.
// + Ruta pública /vehiculo/:id para lectura de QR.
// ======================================================

import { Suspense, lazy, memo, useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import DashboardLayout from "./layouts/DashboardLayout";

// 🏥 PÁGINAS (Lazy)
const Login = lazy(() => import("./pages/Login"));
const RecuperarContrasena = lazy(() => import("./pages/RecuperarContrasena"));
const Sedes = lazy(() => import("./pages/Sedes"));
const Activos = lazy(() => import("./pages/Activos"));
const Vehiculos = lazy(() => import("./pages/Vehiculos"));
const Documentos = lazy(() => import("./pages/Documentos"));
const ReporteAvanzado = lazy(() => import("./pages/ReporteAvanzado"));
const Usuarios = lazy(() => import("./pages/Usuarios"));
const VehiculoDetalle = lazy(() => import("./pages/VehiculoDetalle")); // 🆕 Pública (QR)
const ActivoDetallePublico = lazy(() => import("./pages/ActivoDetallePublico"));

// ⏳ Fallback de carga
const LoadingFallback = memo(function LoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: "2rem",
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        color: "#004aad",
        textAlign: "center"
      }}
    >
      Cargando módulo…
    </div>
  );
});
LoadingFallback.displayName = "LoadingFallback";

// 🧭 Persistencia de última ruta visitada
function PersistirRuta() {
  const location = useLocation();
  const navigate = useNavigate();

  // Guardar cada cambio de ruta (excepto públicas)
  useEffect(() => {
    const pathname = location.pathname;
    const esPublica =
      pathname === "/login" ||
      pathname === "/recuperar" ||
      pathname.startsWith("/vehiculo/") ||
      /^\/activos\/[^/]+$/.test(pathname); // 👈 NO persistimos las rutas públicas del QR

    if (!esPublica) {
      localStorage.setItem("ultimaRuta", pathname);
    }
  }, [location.pathname]);

  // Si entran a "/", redirigir a la última o a /activos
  useEffect(() => {
    if (location.pathname === "/") {
      const ultima = localStorage.getItem("ultimaRuta") || "/activos";
      navigate(ultima, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <PersistirRuta />
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* 🔓 RUTAS PÚBLICAS */}
                <Route path="/login" element={<Login />} />
                <Route path="/recuperar" element={<RecuperarContrasena />} />
                <Route path="/vehiculo/:id" element={<VehiculoDetalle />} /> {/* 🆕 Pública (QR) */}
                <Route path="/activos/:id" element={<ActivoDetallePublico />} />

                {/* 🔒 RUTAS PROTEGIDAS */}
                <Route
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* 🟢 Por defecto ir a Activos */}
                  <Route index element={<Activos />} />
                  <Route path="activos" element={<Activos />} />
                  <Route path="vehiculos" element={<Vehiculos />} />
                  <Route path="documentos" element={<Documentos />} />
                  <Route path="sedes" element={<Sedes />} />
                  <Route path="reportes/avanzado" element={<ReporteAvanzado />} />
                  <Route path="usuarios" element={<Usuarios />} />
                </Route>

                {/* 🧭 CATCH-ALL → Activos */}
                <Route path="*" element={<Navigate to="/activos" replace />} />
              </Routes>
            </Suspense>

            {/* 🔔 Notificaciones globales */}
            <ToastContainer
              position="top-right"
              autoClose={4000}
              newestOnTop={false}
              closeOnClick
              pauseOnFocusLoss
              draggable
              pauseOnHover
              hideProgressBar={false}
              theme="colored"
            />
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

App.displayName = "App";
const MemoizedApp = memo(App);
MemoizedApp.displayName = "MemoizedApp";
export default MemoizedApp;
